<template>
  <div>
    <!-- Toasts normais (topo) -->
    <div class="toast-container">
      <transition-group name="toast-animation" tag="div" class="toast-list">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          class="toast-item" 
          :class="toast.type"
        >
          <div class="toast-accent"></div>
          <div class="toast-icon">
            <CheckCircle v-if="toast.type === 'success'" :size="20" />
            <AlertCircle v-else-if="toast.type === 'error' || toast.type === 'warning'" :size="20" />
            <Info v-else :size="20" />
          </div>
          <div class="toast-content">
            <h4 v-if="toast.title" class="toast-title">{{ toast.title }}</h4>
            <p class="toast-message">{{ toast.message }}</p>
            <button 
              v-if="toast.type === 'error'" 
              class="toast-copy-btn" 
              @click="copyToClipboard(toast.message)"
            >
              <Copy :size="12" />
              <span>Copiar Detalhes</span>
            </button>
          </div>
          <button class="toast-close" @click="close(toast.id)">
            <X :size="16" />
          </button>
        </div>
      </transition-group>
    </div>

    <!-- Toasts centralizados (tipo modal de alerta) -->
    <Transition name="centered-toast-fade">
      <div v-if="centeredToasts.length > 0" class="centered-toast-backdrop" @click="close(centeredToasts[centeredToasts.length - 1].id)">
        <div 
          v-for="toast in centeredToasts" 
          :key="toast.id" 
          class="centered-toast-card"
          :class="toast.type"
          @click.stop
        >
          <button class="centered-toast-close" @click="close(toast.id)">
            <X :size="20" />
          </button>
          
          <div class="centered-toast-body">
            <div class="centered-toast-icon">
              <CheckCircle v-if="toast.type === 'success'" :size="36" />
              <AlertCircle v-else-if="toast.type === 'error' || toast.type === 'warning'" :size="36" />
              <Info v-else :size="36" />
            </div>
            <h3 v-if="toast.title" class="centered-toast-title">{{ toast.title }}</h3>
            <p class="centered-toast-message">{{ toast.message }}</p>
            
            <button class="centered-toast-btn" @click="close(toast.id)">
              Entendi
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
 
<script>
import { CheckCircle, AlertCircle, Info, X, Copy } from 'lucide-vue-next';
 
export default {
  name: 'ToastComp',
  components: {
    CheckCircle,
    AlertCircle,
    Info,
    X,
    Copy
  },
  computed: {
    toasts() {
      return this.$store.getters.activeToasts.filter(t => !t.isCentered);
    },
    centeredToasts() {
      return this.$store.getters.activeToasts.filter(t => t.isCentered);
    }
  },
  methods: {
    close(id) {
      this.$store.dispatch('removeToast', id);
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        // Não criamos outro toast de sucesso que dure muito para não poluir, apenas 1.5s
        this.$store.dispatch('showToast', {
          title: 'Copiado!',
          message: 'Erro copiado com sucesso.',
          type: 'success',
          duration: 1500
        });
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    }
  }
}
</script>

<style lang="less" scoped>
.toast-container {
  position: fixed;
  top: calc(16px + max(12px, env(safe-area-inset-top, 0px)));
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 400px;
  z-index: 10000002; /* Float above everything including CartSheet */
  pointer-events: none;

  .toast-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .toast-item {
    pointer-events: auto;
    display: flex;
    align-items: flex-start;
    background: rgba(10, 25, 47, 0.95);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 20px;
    padding: 14px 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
    max-height: 250px;
    overflow-y: auto;

    .toast-accent {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 5px;
    }

    .toast-icon {
      margin-right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .toast-content {
      flex: 1;
      padding-right: 8px;
      text-align: left;

      .toast-title {
        color: white;
        font-size: 14px;
        font-weight: 700;
        margin-bottom: 4px;
      }

      .toast-message {
        color: rgba(255, 255, 255, 0.9);
        font-size: 13px;
        font-weight: 500;
        line-height: 1.4;
        word-break: break-word;
      }

      .toast-copy-btn {
        margin-top: 10px;
        background: rgba(255, 255, 255, 0.15);
        border: 1px solid rgba(255, 255, 255, 0.25);
        color: white;
        border-radius: 8px;
        padding: 6px 12px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;

        &:hover {
          background: rgba(255, 255, 255, 0.25);
        }

        &:active {
          transform: scale(0.97);
        }
      }
    }

    .toast-close {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.4);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      border-radius: 50%;
      transition: all 0.2s;

      &:hover {
        color: white;
        background: rgba(255, 255, 255, 0.1);
      }
    }

    /* Type styles */
    &.success {
      .toast-accent { background: var(--green); }
      .toast-icon { color: var(--green); }
    }

    &.error {
      .toast-accent { background: #ff3b30; }
      .toast-icon { color: #ff3b30; }
    }

    &.warning {
      .toast-accent { background: var(--orange); }
      .toast-icon { color: var(--orange); }
    }

    &.info {
      .toast-accent { background: var(--blue); }
      .toast-icon { color: var(--blue); }
    }
  }
}

/* Animations */
.toast-animation-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.toast-animation-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.toast-animation-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.toast-animation-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>

<style lang="less" scoped>
.centered-toast-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 10000003;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  pointer-events: auto;
}

.centered-toast-card {
  background: rgba(10, 25, 47, 0.98);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 28px;
  padding: 32px 24px 24px 24px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  position: relative;
  text-align: center;
  animation: centeredScale 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  .centered-toast-close {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    padding: 6px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      color: white;
      background: rgba(255, 255, 255, 0.1);
    }
  }

  .centered-toast-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    .centered-toast-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 4px;
    }

    .centered-toast-title {
      font-size: 18px;
      font-weight: 700;
      color: var(--yellow);
    }

    .centered-toast-message {
      font-size: 15px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
      line-height: 1.5;
    }

    .centered-toast-btn {
      background: var(--yellow);
      color: #005d97;
      border: none;
      border-radius: 18px;
      padding: 12px 32px;
      font-size: 15px;
      font-weight: 800;
      cursor: pointer;
      box-shadow: 0 6px 15px rgba(250, 184, 0, 0.3);
      transition: all 0.2s ease;
      margin-top: 8px;
      width: 100%;

      &:hover {
        background: white;
        transform: translateY(-1px);
        box-shadow: 0 8px 20px rgba(255, 255, 255, 0.2);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  /* Type configurations */
  &.success {
    border-color: rgba(36, 189, 46, 0.4);
    .centered-toast-icon { color: var(--green); }
    .centered-toast-btn {
      background: var(--green);
      color: white;
      box-shadow: 0 6px 15px rgba(36, 189, 46, 0.3);
    }
  }

  &.error {
    border-color: rgba(255, 59, 48, 0.4);
    .centered-toast-icon { color: #ff3b30; }
    .centered-toast-btn {
      background: #ff3b30;
      color: white;
      box-shadow: 0 6px 15px rgba(255, 59, 48, 0.3);
    }
  }

  &.warning {
    border-color: rgba(246, 102, 6, 0.4);
    .centered-toast-icon { color: var(--orange); }
    .centered-toast-btn {
      background: var(--orange);
      color: white;
      box-shadow: 0 6px 15px rgba(246, 102, 6, 0.3);
    }
  }

  &.info {
    border-color: rgba(0, 158, 255, 0.4);
    .centered-toast-icon { color: var(--blue); }
    .centered-toast-btn {
      background: var(--blue);
      color: white;
      box-shadow: 0 6px 15px rgba(0, 158, 255, 0.3);
    }
  }
}

@keyframes centeredScale {
  from {
    transform: scale(0.85);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Backdrop Fade Transitions */
.centered-toast-fade-enter-active, .centered-toast-fade-leave-active {
  transition: opacity 0.25s ease;
}
.centered-toast-fade-enter-from, .centered-toast-fade-leave-to {
  opacity: 0;
}
</style>
